import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Users, Leaf, Award, Target, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const AboutUs = () => {
  const values = [
    {
      icon: Heart,
      title: "Passion for Clean",
      description: "We don't just clean spaces—we transform them. Our team takes genuine pride in every spotless surface, every fresh-smelling room, and every satisfied smile."
    },
    {
      icon: Users,
      title: "Family First",
      description: "We understand that your home is your sanctuary. That's why we treat every space as if it were our own, with care, respect, and attention to detail."
    },
    {
      icon: Leaf,
      title: "Eco-Conscious",
      description: "We believe clean shouldn't come at the cost of our planet. Our eco-friendly products are safe for your family, pets, and the environment."
    },
    {
      icon: Award,
      title: "Excellence Always",
      description: "Good enough isn't in our vocabulary. We hold ourselves to the highest standards because you deserve nothing less than exceptional."
    }
  ];

  const stats = [
    { number: "8+", label: "Years of Experience" },
    { number: "500+", label: "Happy Families" },
    { number: "100+", label: "Business Partners" },
    { number: "10,000+", label: "Cleanings Completed" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#1D4ED8] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-4">
                About Us
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                More Than Cleaning—
                <span className="gradient-text">We Create Balance</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                At River City Cleaners, we believe that a clean space is the foundation 
                for a balanced life. When your home sparkles and your office shines, 
                you're free to focus on what truly matters—your family, your work, your passions.
              </p>
              <Button
                onClick={() => window.location.href = "/#booking"}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="book-now-btn"
              >
                Experience the Difference
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800"
                alt="Our cleaning team"
                className="w-full h-[450px] object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6">
                <p className="font-accent text-2xl text-[#2563EB] mb-1">Our Promise</p>
                <p className="text-slate-600 text-sm">Your satisfaction, guaranteed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Team collaboration"
                className="w-full h-[400px] object-cover rounded-3xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
                Our Story
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6">
                Born from a Simple Belief
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  River City Cleaners was founded on a simple yet powerful belief: everyone 
                  deserves to come home to a clean, healthy space without sacrificing their 
                  precious time or peace of mind.
                </p>
                <p>
                  As a proud division of NBRents.ca, we've seen firsthand how a well-maintained 
                  space can transform not just properties, but lives. We watched busy parents 
                  struggle to balance work and home. We saw business owners stressed about 
                  maintaining professional appearances. We knew there had to be a better way.
                </p>
                <p>
                  That's why we built a cleaning service that goes beyond wiping surfaces. 
                  We're in the business of giving you back your time, reducing your stress, 
                  and creating environments where families can thrive and businesses can flourish.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
              Our Mission
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6">
              Helping You Find Balance
            </h2>
            <p className="text-lg text-slate-600">
              Life is busy. Between work deadlines, family commitments, and the endless 
              to-do list, cleaning often falls to the bottom of priorities—but the stress 
              of a cluttered space never does. We're here to lift that burden.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100">
              <div className="w-14 h-14 bg-[#2563EB]/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#2563EB]" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">For Families</h3>
              <p className="text-slate-600 mb-4">
                We know that after a long day, the last thing you want is to spend your 
                evening scrubbing floors. Your weekends should be for making memories 
                with loved ones, not catching up on chores.
              </p>
              <p className="text-slate-600">
                Our residential services give you the gift of time—time to play with your 
                kids, enjoy a quiet dinner, or simply relax in a home that feels like a 
                true retreat.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100">
              <div className="w-14 h-14 bg-[#3B82F6]/10 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-[#2563EB]" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">For Businesses</h3>
              <p className="text-slate-600 mb-4">
                First impressions matter. A clean, well-maintained workspace tells your 
                clients and employees that you care about quality and professionalism 
                in everything you do.
              </p>
              <p className="text-slate-600">
                Our commercial services help you create an environment where productivity 
                thrives, employees feel valued, and customers feel confident in choosing 
                your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
              What Drives Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
              Our Core Values
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6"
                  data-testid={`value-${index}`}
                >
                  <div className="w-16 h-16 bg-[#2563EB]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-[#2563EB]" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#2563EB] to-[#0891b2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Trusted by Families & Businesses Across Miramichi
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-5xl font-bold text-white mb-2">{stat.number}</p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Philosophy Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
                Our Team
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6">
                The Heart Behind the Clean
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Our team isn't just skilled—they're passionate. Every cleaner at River 
                  City Cleaners is carefully selected not only for their attention to detail 
                  but for their genuine care for the people they serve.
                </p>
                <p>
                  We invest in our team because we know that happy employees create happy 
                  customers. That means fair wages, ongoing training, and a supportive 
                  work environment that makes them proud to wear the River City Cleaners name.
                </p>
                <p>
                  When our team walks into your space, they bring more than cleaning 
                  supplies—they bring dedication, respect, and a commitment to excellence 
                  that you can see in every sparkling surface.
                </p>
              </div>
              <div className="mt-8 p-6 bg-slate-50 rounded-2xl">
                <p className="text-slate-700 italic">
                  "We don't just clean houses—we help families reclaim their time and peace 
                  of mind. That's what gets me excited to come to work every day."
                </p>
                <p className="text-[#2563EB] font-semibold mt-4">— The River City Team</p>
              </div>
              <Button
                onClick={() => window.location.href = "/team"}
                className="mt-6 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-4 font-semibold"
                data-testid="meet-team-btn"
              >
                Meet Our Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to Experience the River City Difference?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join the hundreds of families and businesses who've discovered 
            what it means to truly relax in a clean space. Let us handle the 
            cleaning while you focus on living.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = "/#booking"}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="cta-book-btn"
            >
              Get Your Free Quote
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = "tel:506-210-3395"}
              className="border-slate-200 rounded-full px-8 py-6 text-lg"
              data-testid="cta-call-btn"
            >
              Call 506-210-3395
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
